import React, { useState, useEffect } from "react";
import { useAuth } from "../../Auth/AuthPage.jsx";
import { Paper, Typography, TextField, Button, List, ListItem, ListItemText, Alert, IconButton, Box, Grid, Link } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import {Crumbs} from "../../Crumbs.jsx";
import LoginRequired from "../../LoginRequired.jsx";
import { Spotify } from "react-spotify-embed";

const BACKEND_HOST = process.env["REACT_APP_BACKEND_HOST"];

const PlaylistPage = () => {
    // Helper to check if form is valid
    const isFormValid = () => {
        if (!form.title.trim()) return false;
        if (!form.link.trim()) return false;
        const spotifyPattern = /^https:\/\/(open\.)?spotify\.com\//i;
        if (!spotifyPattern.test(form.link.trim())) return false;
        return true;
    };
    const { user, token, hasUserProfile } = useAuth();
    const [playlists, setPlaylists] = useState([]);
    const [form, setForm] = useState({ title: "", link: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const loadPlaylists = () => {
        if (user && token) {
            fetch(`${BACKEND_HOST}/playlists/mine`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then(res => res.json())
                .then(data => setPlaylists(data || []))
                .catch(() => setError("Failed to load playlists"));
        }
    }

    useEffect(() => {
        loadPlaylists()
    }, []);

    const handleChange = (e) => { 
        setForm({ ...form, [e.target.name]: e.target.value });
        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            setError("Playlist title is required.");
            return;
        }
        if (!form.link.trim()) {
            setError("Playlist link is required.");
            return;
        }
        // Validate Spotify link
        const spotifyPattern = /^https?:\/\/(open\.)?spotify\.com\//i;
        if (!spotifyPattern.test(form.link.trim())) {
            setError("Playlist link must be an open.spotify.com link.");
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${BACKEND_HOST}/playlists`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    data: {
                        title: form.title,
                        link: form.link
                    },
                }),
            });
            if (res.status !== 200) throw new Error("Failed to add playlist");
            loadPlaylists();
            setError("")
            setForm({ title: "", link: "" });
            setSuccess("Playlist added!");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            const res = await fetch(`${BACKEND_HOST}/playlists/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (res.status !== 200) throw new Error("Failed to delete playlist");
            loadPlaylists();
            setSuccess("Playlist deleted!");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

 

    return (
        <Box sx={{ mt: 2 }}>
            <Crumbs crumbs={
                [{ title: "Community", href: "/community" }, 
                { title: "Playlists", href: "/community/playlists" },
                { title: "My Playlists" }
            ]
            } />
            <LoginRequired>
                <Grid container spacing={2}>
                    <Grid item xs={12} sx={{ height: "100%" }}>
                        {/* <Paper style={{ margin: "auto", height: "100%" }}> */}
                            <Box py={3}>
                                <Typography variant="h4" align="center" gutterBottom>
                                    Your Playlists
                                </Typography>
                                <Typography variant="body2" align="center" gutterBottom>
                                    Got a great playlist? Share it with the community! Don't let it go unnoticed.
                                </Typography>
                                <Box fullWidth textAlign="center" pt={3}>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        sx={{ mb: 2 }}
                                        href="/community/playlists">

                                        View Community Playlists
                                    </Button>
                                </Box>
                            </Box>
                        {/* </Paper> */}
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                        <Paper style={{ padding: 20, margin: "auto", height: "100%" }}>
                            <Typography variant="h6" align="center" gutterBottom>
                                Add a Playlist
                            </Typography>
                            <Typography variant="subtitle2" color={"text.secondary"} gutterBottom>
                                {hasUserProfile ? "Your playlists will include your public profile. This can be adjusted in your profile settings." : "Your playlists will be public, but will not be attributed to you since you do not have a public profile."}
                            </Typography>
                            <form onSubmit={handleSubmit} style={{ marginBottom: 20 }}>
                                <TextField
                                    label="Playlist Title"
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Spotify Link"
                                    name="link"
                                    value={form.link}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading || !isFormValid() || !hasUserProfile}
                                    fullWidth
                                >
                                    {loading ? "Adding..." : "Add Playlist"}
                                </Button>
                                {!hasUserProfile && (
                                    <Alert severity="info" style={{ marginTop: 10 }}>
                                        You must create a public profile before adding a playlist.
                                        See your account page at <Link href="/account">/account</Link> to add a public profile.
                                    </Alert>
                                )}
                                {error && <Alert severity="error" style={{ marginTop: 10 }}>{error}</Alert>}
                                {success && <Alert severity="success" style={{ marginTop: 10 }}>{success}</Alert>}
                            </form>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                        <Box style={{ top: -25, position: "relative", height: "100%" }}>
                            <List>
                                {playlists.map((pl) => (
                                    <ListItem
                                        key={pl.id}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(pl.documentId)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        }>
                                        <ListItemText primary={pl.title} secondary={<Spotify link={pl.link} wide />} />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                       
                    </Grid>
                </Grid>
            </LoginRequired>
        </Box>
    );
};

export default PlaylistPage;